import { useState } from "react";
import { NavLink, useNavigate } from "@/routing/accountRouting";
import { useAuth } from "@/context/AuthContext";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { HiddenFromCustomerMark } from "@/components/auth/HiddenFromCustomer";
import {
  IconUser,
  IconUsers,
  IconBriefcase,
  IconLayersLinked,
  IconBook,
  IconBuilding,
  IconBuildingStore,
  IconWorld,
  IconSettings,
  IconChevronLeft,
  IconChevronRight,
  IconMessageCircle,
  IconRocket,
  IconSeeding,
  IconArrowLeft,
  IconStar,
  IconFlask,
  IconActivity,
  IconChartBar,
  IconPlugConnected,
  IconApps,
  IconHeartbeat,
  IconLayoutKanban,
  IconChartArrows,
} from "@tabler/icons-react";
import "./SettingsSidebar.css";

export default function SettingsSidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isSuperadmin = user?.isSuperadmin ?? false;
  // White-label partner-admin: sees the (scoped) Clients view but no platform
  // management or the partner registry.
  const isPartnerAdmin = user?.isPartnerAdmin ?? false;
  const role = user?.activeAccount?.role;
  const isOwnerOrAdmin = role === "OWNER" || role === "ADMIN";

  // Per-account visibility for the gateable settings items, by the same rule
  // as the module nav: a superadmin sees every item, marked when the account's
  // members don't (useSectionAccess). Core items (Account/Users/Workspaces/
  // Profile) are not gated and are intentionally absent from the "settings"
  // registry section.
  const sectionAccess = useSectionAccess();
  const showSettingsItem = (key: string) => sectionAccess.submenu("settings", key).shown;
  const hiddenMark = (key: string) =>
    sectionAccess.submenu("settings", key).hiddenFromCustomer ? <HiddenFromCustomerMark /> : null;
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("settings-sidebar-collapsed");
    return stored === "true";
  });

  const toggleCollapsed = () => {
    const newValue = !collapsed;
    setCollapsed(newValue);
    localStorage.setItem("settings-sidebar-collapsed", String(newValue));
  };

  const handleBack = () => {
    // Return to the page the user was on before entering Settings
    const returnPath = sessionStorage.getItem("settings-return-path") || "/";
    sessionStorage.removeItem("settings-return-path");
    navigate(returnPath);
  };

  return (
    <div className={`settings-sidebar ${collapsed ? "collapsed" : ""}`}>
      <div className="settings-sidebar-top">
        <button
          className="settings-back-btn"
          onClick={handleBack}
          title="Back"
        >
          <IconArrowLeft size={16} />
          {!collapsed && <span>Back</span>}
        </button>
        <button
          className="settings-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
        </button>
      </div>

      <nav className="settings-nav">
        <div className="settings-section">
          <div className="settings-section-header">You</div>
          <NavLink to="/settings/profile" className="settings-link" title="Profile">
            <IconUser size={16} />
            <span className="settings-link-text">Profile</span>
          </NavLink>
        </div>

        {/* Account section. Account/Users/Workspaces are ungated core items and
            always render, so this header is never empty. The remaining items
            also respect per-account settings-menu visibility (showSettingsItem). */}
        <div className="settings-section">
          <div className="settings-section-header">Account</div>
          <NavLink to="/settings/account" className="settings-link" title="Account">
            <IconBuilding size={16} />
            <span className="settings-link-text">Account</span>
          </NavLink>
          <NavLink to="/settings/users" className="settings-link" title="Users">
            <IconUsers size={16} />
            <span className="settings-link-text">Users</span>
          </NavLink>
          <NavLink to="/settings/workspaces" className="settings-link" title="Workspaces">
            <IconBriefcase size={16} />
            <span className="settings-link-text">Workspaces</span>
          </NavLink>
          {showSettingsItem("mcp") && (
            <NavLink to="/settings/mcp" className="settings-link" title="MCP Connections">
              <IconPlugConnected size={16} />
              <span className="settings-link-text">MCP Connections</span>
              {hiddenMark("mcp")}
            </NavLink>
          )}
          {(isOwnerOrAdmin || isSuperadmin) && showSettingsItem("integrations") && (
            <NavLink to="/settings/integrations" className="settings-link" title="Integrations">
              <IconApps size={16} />
              <span className="settings-link-text">Integrations</span>
              {hiddenMark("integrations")}
            </NavLink>
          )}
          {(isOwnerOrAdmin || isSuperadmin) && showSettingsItem("marketing-channels") && (
            <NavLink to="/settings/marketing-channels" className="settings-link" title="Marketing Channels">
              <IconChartArrows size={16} />
              <span className="settings-link-text">Marketing Channels</span>
              {hiddenMark("marketing-channels")}
            </NavLink>
          )}
          {showSettingsItem("preferences") && (
            <NavLink to="/settings/preferences" className="settings-link" title="Preferences">
              <IconSettings size={16} />
              <span className="settings-link-text">Preferences</span>
              {hiddenMark("preferences")}
            </NavLink>
          )}
          {(isOwnerOrAdmin || isSuperadmin) && showSettingsItem("analyst-performance") && (
            <NavLink to="/settings/analyst-performance" className="settings-link" title="Analyst Performance">
              <IconChartBar size={16} />
              <span className="settings-link-text">Analyst Performance</span>
              {hiddenMark("analyst-performance")}
            </NavLink>
          )}
        </div>

        {(isSuperadmin || isPartnerAdmin) && (
          <div className="settings-section">
            <div className="settings-section-header">
              {isSuperadmin ? "Platform" : "Management"}
            </div>
            <NavLink to="/settings/clients" className="settings-link" title="Clients">
              <IconBuilding size={16} />
              <span className="settings-link-text">Clients</span>
            </NavLink>
            {isSuperadmin && (
              <>
            <NavLink to="/settings/partners" className="settings-link" title="Partners">
              <IconBuildingStore size={16} />
              <span className="settings-link-text">Partners</span>
            </NavLink>
            <NavLink to="/platform-settings/agents" className="settings-link" title="Platform Agents">
              <IconWorld size={16} />
              <span className="settings-link-text">Platform Agents</span>
            </NavLink>
            <NavLink to="/platform-settings/semantic-layer" className="settings-link" title="Platform Semantic Layer">
              <IconLayersLinked size={16} />
              <span className="settings-link-text">Platform Semantics</span>
            </NavLink>
            <NavLink to="/platform-settings/glossary" className="settings-link" title="Platform Glossary">
              <IconBook size={16} />
              <span className="settings-link-text">Platform Glossary</span>
            </NavLink>
            <NavLink to="/platform-settings/preferences" className="settings-link" title="Platform Preferences">
              <IconSettings size={16} />
              <span className="settings-link-text">Platform Preferences</span>
            </NavLink>
            <NavLink to="/platform-settings/pipelines" className="settings-link" title="Pipelines">
              <IconRocket size={16} />
              <span className="settings-link-text">Pipelines</span>
            </NavLink>
            <NavLink to="/platform-settings/seeds" className="settings-link" title="Seeds">
              <IconSeeding size={16} />
              <span className="settings-link-text">Seeds</span>
            </NavLink>
            <NavLink to="/platform-settings/golden-questions" className="settings-link" title="Golden Questions">
              <IconStar size={16} />
              <span className="settings-link-text">Golden Questions</span>
            </NavLink>
            <NavLink to="/platform-settings/evals" className="settings-link" title="Agent Evals">
              <IconFlask size={16} />
              <span className="settings-link-text">Agent Evals</span>
            </NavLink>
            <NavLink to="/platform-settings/feedback" className="settings-link" title="User Feedback">
              <IconMessageCircle size={16} />
              <span className="settings-link-text">User Feedback</span>
            </NavLink>
            <NavLink to="/platform-settings/chat-monitor" className="settings-link" title="Chat Monitor">
              <IconActivity size={16} />
              <span className="settings-link-text">Chat Monitor</span>
            </NavLink>
            <NavLink to="/platform-settings/ops" className="settings-link" title="Ops Dashboard">
              <IconHeartbeat size={16} />
              <span className="settings-link-text">Ops</span>
            </NavLink>
            <NavLink to="/platform-settings/orchestration" className="settings-link" title="Orchestration Board">
              <IconLayoutKanban size={16} />
              <span className="settings-link-text">Orchestration</span>
            </NavLink>
              </>
            )}
          </div>
        )}
      </nav>
    </div>
  );
}
